/**
 * Reel Generation Hook
 * Feature 017: Instagram Reel Creation
 * Orchestrates the entire reel generation flow:
 * 1. Research topic with Perplexity
 * 2. Generate reels from 3 AI models in parallel
 * 3. Update UI progressively as each model completes
 */

import { useAction, useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Id } from '../../convex/_generated/dataModel';
import type { ModelKey, IReelModelResponse } from '../@types/reels';
import { useReelGenerationState } from './useReelGenerationState';

/**
 * useReelGenerationMultiAI Hook
 * Orchestrates the entire reel generation flow for Feature 017:
 * 1. Research topic with Perplexity
 * 2. Generate reel scripts from 3 AI models in parallel
 * 3. Update UI progressively as each model completes
 */
export function useReelGenerationMultiAI(reelId: Id<'reels'> | undefined, topicId: Id<'topics'>) {
  const { state, dispatch } = useReelGenerationState(reelId);
  const navigate = useNavigate();

  // Fetch business profile and AI preferences from unified businesses table
  const businessProfile = useQuery(api.businesses.queries.getMyBusinessProfile, {});
  const aiPreferences = useQuery(
    api.settings.aiPreferences.getAiModelPreferences,
    businessProfile ? { businessId: businessProfile._id } : 'skip'
  );

  // Extract selected models from preferences (only dispatch when models actually change)
  useEffect(() => {
    if (!aiPreferences) return;

    const selectedModels: ModelKey[] = [];
    if (aiPreferences.useClaude) selectedModels.push('claude');
    if (aiPreferences.useGemini) selectedModels.push('gemini');
    if (aiPreferences.useGPT) selectedModels.push('openai');

    // Skip dispatch if models haven't changed to avoid resetting generation status
    const currentModels = state.selectedModels;
    if (
      selectedModels.length === currentModels.length &&
      selectedModels.every((m, i) => m === currentModels[i])
    ) {
      return;
    }

    dispatch({ type: 'SET_SELECTED_MODELS', payload: { models: selectedModels } });
  }, [aiPreferences, dispatch, state.selectedModels]);

  // Actions
  const researchTopicAction = useAction(api.ai.reelGeneration.researchTopicForReel);
  const generateSingleModelReelAction = useAction(api.ai.reelGeneration.generateSingleModelReel);
  const selectBestVariantAction = useMutation(api.reels.selectBestReelVariant);

  /**
   * Handle Start Generation
   * Orchestrates research -> multi-model generation flow
   */
  const handleStartGeneration = useCallback(async () => {
    if (!reelId) return;

    // Guard against race condition: ensure models are loaded
    if (state.selectedModels.length === 0) {
      console.warn('No AI models selected for generation');
      return;
    }

    try {
      // Initialize reel state
      dispatch({ type: 'INIT_REEL', payload: { reelId } });

      // Step 1: Research phase
      dispatch({ type: 'START_RESEARCH' });

      const researchResult = await researchTopicAction({
        reelId,
        topicId,
      });

      dispatch({
        type: 'RESEARCH_SUCCESS',
        payload: {
          researchResults: researchResult.researchResults,
          cached: researchResult.cached,
        },
      });

      // Step 2: Start multi-model generation
      dispatch({ type: 'START_GENERATION' });

      // Progressive dispatch logic - Generate only for selected models
      const modelPromises = state.selectedModels.map(async modelKey => {
        // Mark model as loading
        dispatch({ type: 'MODEL_GENERATION_START', payload: { modelKey } });

        try {
          const result = await generateSingleModelReelAction({
            reelId,
            modelKey,
            researchResults: researchResult.researchResults,
            topicId,
          });

          // Check if generation failed (error field present)
          if (result.error) {
            dispatch({
              type: 'MODEL_GENERATION_ERROR',
              payload: {
                modelKey,
                error: result.error,
              },
            });
          } else {
            // Dispatch success as soon as this model completes
            dispatch({
              type: 'MODEL_GENERATION_SUCCESS',
              payload: {
                modelKey,
                reelData: {
                  content: result.content,
                  model: result.model,
                  timestamp: result.timestamp,
                  cached: result.cached,
                } as IReelModelResponse,
              },
            });
          }
        } catch (error) {
          // Dispatch error for unexpected exception
          dispatch({
            type: 'MODEL_GENERATION_ERROR',
            payload: {
              modelKey,
              error: error instanceof Error ? error.message : 'Generation failed',
            },
          });
        }
      });

      // Wait for all models to complete (or fail)
      await Promise.allSettled(modelPromises);

      // Mark all models complete
      dispatch({ type: 'ALL_MODELS_COMPLETE' });
    } catch (error) {
      // Research phase error
      dispatch({
        type: 'RESEARCH_ERROR',
        payload: {
          error: error instanceof Error ? error.message : 'Research failed',
        },
      });
    }
  }, [
    reelId,
    topicId,
    state.selectedModels,
    dispatch,
    researchTopicAction,
    generateSingleModelReelAction,
  ]);

  /**
   * Handle Select Best Variant
   * Saves user's choice and navigates to reel editor
   */
  const handleSelectBestVariant = useCallback(
    async (modelKey: ModelKey) => {
      if (!reelId) return;

      dispatch({ type: 'START_VARIANT_SELECTION' });

      try {
        // Pass content from frontend state as fallback for when DB generatedReels is empty
        const variantData = state.generatedReels[modelKey];
        const result = await selectBestVariantAction({
          reelId,
          modelKey,
          content: variantData?.content,
        });

        if (result.success) {
          dispatch({ type: 'VARIANT_SELECTION_SUCCESS', payload: { modelKey } });
          navigate(`/reel/${reelId}`);
        } else {
          dispatch({
            type: 'VARIANT_SELECTION_ERROR',
            payload: {
              error: result.error || 'Selection failed',
            },
          });
        }
      } catch (error) {
        dispatch({
          type: 'VARIANT_SELECTION_ERROR',
          payload: {
            error: error instanceof Error ? error.message : 'Selection failed',
          },
        });
      }
    },
    [reelId, state.generatedReels, selectBestVariantAction, dispatch, navigate]
  );

  /**
   * Reset state
   */
  const handleReset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, [dispatch]);

  return {
    state,
    handleStartGeneration,
    handleSelectBestVariant,
    handleReset,
    businessProfile,
    isReady: !!businessProfile && !!aiPreferences && state.selectedModels.length > 0,
  };
}

/**
 * useReelById Hook
 * Fetches a single reel by ID with real-time updates
 */
export function useReelById(reelId: Id<'reels'> | undefined) {
  const reel = useQuery(api.reels.getReelById, reelId ? { reelId } : 'skip');

  return {
    reel,
    isLoading: reel === undefined,
    notFound: reel === null,
  };
}
